import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Dumbbell, Apple, Scale, Trophy, Pencil } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { API } from '../../App';
import HamburgerMenu from '../../components/HamburgerMenu';

const Stat = ({ label, value, sub }) => (
  <div className="bg-stone-900/60 rounded-lg p-3">
    <p className="text-[10px] uppercase tracking-widest text-stone-500">{label}</p>
    <p className="text-xl font-bold text-white">{value}</p>
    {sub && <p className="text-[11px] text-stone-500">{sub}</p>}
  </div>
);

export default function AthleteDetailView() {
  const { orgId, athleteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ daily_calories: '', daily_protein: '', daily_carbs: '', daily_fats: '' });
  const [savingGoals, setSavingGoals] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setData(null); // never retain previously authorized athlete data after a scope change
    axios.get(`${API}/coach/${orgId}/athletes/${athleteId}?days=${days}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load athlete.'))
      .finally(() => setLoading(false));
  }, [orgId, athleteId, days]);

  useEffect(() => { load(); }, [load]);

  const openGoals = () => {
    const g = data?.nutrition?.goals || {};
    setGoalForm({
      daily_calories: g.daily_calories || 2000,
      daily_protein: g.daily_protein || 150,
      daily_carbs: g.daily_carbs || 200,
      daily_fats: g.daily_fats || 65,
    });
    setGoalsOpen(true);
  };

  const saveGoals = async (e) => {
    e.preventDefault();
    setSavingGoals(true);
    try {
      await axios.put(`${API}/coach/${orgId}/athletes/${athleteId}/nutrition-goals`, {
        daily_calories: parseFloat(goalForm.daily_calories),
        daily_protein: parseFloat(goalForm.daily_protein),
        daily_carbs: parseFloat(goalForm.daily_carbs),
        daily_fats: parseFloat(goalForm.daily_fats),
      });
      toast.success('Nutrition goals updated for this athlete.');
      setGoalsOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update goals.');
    } finally {
      setSavingGoals(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900">
        <HamburgerMenu />
        <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900">
        <HamburgerMenu />
        <div className="max-w-xl mx-auto p-6 text-center">
          <p className="text-blood-400 font-bold mb-4">{error}</p>
          <Button onClick={() => navigate('/teams')} className="bg-stone-800 text-stone-200">Back to Teams</Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { athlete, summary, workout, nutrition, body } = data;
  const sharedScopes = new Set(data.shared_scopes || []);
  const bodyShared = sharedScopes.has('body_metrics');
  const photosShared = sharedScopes.has('progress_photos');
  const canEditGoals = Boolean(nutrition && data.can_edit_nutrition_goals);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900" data-testid="athlete-detail-page">
      <HamburgerMenu />
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <Button variant="outline" onClick={() => navigate('/teams')} className="glass border-stone-700 text-stone-300 hover:bg-stone-800" data-testid="athlete-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="flex gap-1.5">
            {[7, 30, 90].map((d) => (
              <button
                key={d} type="button" onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-bold rounded-md border transition-all ${days === d ? 'bg-amber-800 border-amber-600 text-white' : 'bg-stone-900 border-stone-700 text-stone-400 hover:text-white'}`}
                data-testid={`days-filter-${d}`}
              >
                {d}D
              </button>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider">{athlete.username}</h1>
            <span className={`w-2.5 h-2.5 rounded-full ${summary.active_today ? 'bg-emerald-400' : summary.inactive_3plus_days ? 'bg-blood-500' : 'bg-stone-500'}`} />
          </div>
          <p className="text-stone-500 text-sm mt-1">
            {athlete.email} · {(athlete.teams || []).map((t) => t.name).join(', ') || 'No team'}
          </p>
        </div>

        {/* Workout metrics */}
        {workout ? (
        <Card className="glass border-stone-800 p-5 mb-4">
          <h2 className="text-sm font-bold text-white tracking-wider uppercase mb-3 flex items-center gap-2"><Dumbbell className="w-4 h-4 text-amber-400" />Workout Metrics ({days}d)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Completed" value={workout.completed_workouts} />
            <Stat label="Missed" value={workout.missed_workouts} sub={`${workout.planned_workouts} planned`} />
            <Stat label="Compliance (7d)" value={`${workout.compliance_pct}%`} />
            <Stat label="Last Workout" value={workout.last_workout_date || 'never'} />
          </div>

          {workout.prs?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-[11px] uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-amber-400" />Personal Records</h3>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {workout.prs.map((pr) => (
                  <div key={pr.exercise_name} className="flex justify-between text-sm bg-stone-900/60 rounded px-3 py-1.5">
                    <span className="text-stone-300 truncate">{pr.exercise_name}</span>
                    <span className="text-amber-400 font-bold shrink-0 ml-2">{pr.max_weight}{body?.weight_unit ? ` ${body.weight_unit}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {workout.recent_workouts?.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Recent Workouts</h3>
              <div className="space-y-1.5">
                {workout.recent_workouts.slice(0, 8).map((w, i) => (
                  <div key={i} className="text-sm bg-stone-900/60 rounded px-3 py-2">
                    <div className="flex justify-between">
                      <span className="text-stone-200 font-medium">{w.date}</span>
                      <span className="text-stone-500 text-xs">{w.exercise_count} exercises · {w.workout_type}</span>
                    </div>
                    <p className="text-[11px] text-stone-500 mt-0.5 truncate">
                      {(w.exercises || []).map((e) => e.name).filter(Boolean).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
        ) : (
          <Card className="glass border-stone-800 p-5 mb-4">
            <p className="text-stone-400 text-sm">Workout data not shared by this athlete.</p>
          </Card>
        )}

        {/* Nutrition metrics */}
        {nutrition ? (
        <Card className="glass border-stone-800 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-white tracking-wider uppercase flex items-center gap-2"><Apple className="w-4 h-4 text-emerald-400" />Nutrition Metrics ({days}d)</h2>
            {canEditGoals && (
              <Button size="sm" onClick={openGoals} variant="outline" className="border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 h-8" data-testid="edit-goals-btn">
                <Pencil className="w-3.5 h-3.5 mr-1.5" />Adjust Goals
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            <Stat label="Avg Calories" value={nutrition.avg_calories} sub={nutrition.goals ? `goal ${Math.round(nutrition.goals.daily_calories)}` : null} />
            <Stat label="Avg Protein" value={`${nutrition.avg_protein}g`} sub={nutrition.goals ? `goal ${Math.round(nutrition.goals.daily_protein)}g` : null} />
            <Stat label="Avg Carbs" value={`${nutrition.avg_carbs}g`} />
            <Stat label="Avg Fats" value={`${nutrition.avg_fats}g`} />
            <Stat label="Compliance (7d)" value={`${nutrition.compliance_pct}%`} />
          </div>
          {nutrition.daily?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-800">
                    <th className="text-left py-2 pr-3">Date</th>
                    <th className="text-right py-2 px-2">Calories</th>
                    <th className="text-right py-2 px-2">Protein</th>
                    <th className="text-right py-2 px-2">Carbs</th>
                    <th className="text-right py-2 px-2">Fats</th>
                    <th className="text-right py-2 px-2">Meals</th>
                    <th className="text-right py-2 pl-2">Water</th>
                  </tr>
                </thead>
                <tbody>
                  {nutrition.daily.slice(0, 14).map((d) => (
                    <tr key={d.date} className="border-b border-stone-900 text-stone-300">
                      <td className="py-1.5 pr-3">{d.date}</td>
                      <td className="text-right py-1.5 px-2">{Math.round(d.calories)}</td>
                      <td className="text-right py-1.5 px-2">{Math.round(d.protein)}g</td>
                      <td className="text-right py-1.5 px-2">{Math.round(d.carbs)}g</td>
                      <td className="text-right py-1.5 px-2">{Math.round(d.fats)}g</td>
                      <td className="text-right py-1.5 px-2">{d.meals}</td>
                      <td className="text-right py-1.5 pl-2">{nutrition.water_by_day?.[d.date] || 0} 💧</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-stone-500 text-sm">No nutrition logs in this period.</p>
          )}
        </Card>
        ) : (
          <Card className="glass border-stone-800 p-5 mb-4">
            <p className="text-stone-400 text-sm">Nutrition data not shared by this athlete.</p>
          </Card>
        )}

        {/* Body metrics */}
        {body ? (
        <Card className="glass border-stone-800 p-5">
          <h2 className="text-sm font-bold text-white tracking-wider uppercase mb-3 flex items-center gap-2"><Scale className="w-4 h-4 text-sky-400" />Body &amp; Progress</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {bodyShared && <Stat label="Latest Weight" value={body.latest_weight != null ? `${body.latest_weight} ${body.weight_unit}` : '—'} />}
            {bodyShared && <Stat label="Change (90d)" value={body.weight_change_90d != null ? `${body.weight_change_90d > 0 ? '+' : ''}${body.weight_change_90d} ${body.weight_unit}` : '—'} />}
            {photosShared && <Stat label="Progress Photos" value={body.progress_photo_count} />}
          </div>
          {bodyShared && (body.weight_series?.length >= 2 ? (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={body.weight_series} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                  <XAxis dataKey="date" tick={{ fill: '#78716c', fontSize: 10 }} />
                  <YAxis domain={['auto', 'auto']} tick={{ fill: '#78716c', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#fff' }} />
                  <Line type="monotone" dataKey="weight" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-stone-500 text-sm">Not enough weight logs to chart a trend yet.</p>
          ))}
          {!bodyShared && <p className="text-stone-500 text-sm">Weight and body measurements are not shared.</p>}
        </Card>
        ) : (
          <Card className="glass border-stone-800 p-5 mb-4">
            <p className="text-stone-400 text-sm">Body and progress data not shared by this athlete.</p>
          </Card>
        )}
      </div>

      {/* Goals modal */}
      {goalsOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !savingGoals && setGoalsOpen(false)}>
          <Card className="glass border-stone-800 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="goals-modal">
            <h3 className="text-lg font-bold text-white tracking-wider mb-4">ADJUST NUTRITION GOALS</h3>
            <form onSubmit={saveGoals} className="space-y-3">
              {[
                ['daily_calories', 'Daily Calories'],
                ['daily_protein', 'Daily Protein (g)'],
                ['daily_carbs', 'Daily Carbs (g)'],
                ['daily_fats', 'Daily Fats (g)'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">{label}</label>
                  <Input
                    type="number" min="0" step="1"
                    value={goalForm[key]}
                    onChange={(e) => setGoalForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="bg-stone-900 border-stone-700 text-white"
                    data-testid={`goal-input-${key}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setGoalsOpen(false)} disabled={savingGoals} className="flex-1 border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800">Cancel</Button>
                <Button type="submit" disabled={savingGoals} className="flex-1 bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="goals-save-btn">
                  {savingGoals ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Goals'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
